import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Mic, 
  Briefcase, 
  Compass,
  Sparkles,
  ArrowRight
} from 'lucide-react';

interface DailyGreetingPageProps {
  onDismiss: () => void;
}

const quickActions = [
  {
    label: 'Practice Interview',
    description: 'AI-powered mock interviews',
    icon: Mic,
    path: '/interview',
    gradient: 'from-brand-blue to-blue-500',
  },
  {
    label: 'Build Resume',
    description: 'Create ATS-friendly resumes',
    icon: FileText,
    path: '/resume-builder',
    gradient: 'from-orange-500 to-amber-500',
  },
  {
    label: 'Explore Jobs',
    description: 'Find your perfect role',
    icon: Briefcase,
    path: '/job-hunt',
    gradient: 'from-emerald-500 to-teal-500',
  },
  {
    label: 'Career Discovery',
    description: 'Discover your career path',
    icon: Compass,
    path: '/career-discovery',
    gradient: 'from-violet-500 to-purple-500',
  },
];

export function DailyGreetingPage({ onDismiss }: DailyGreetingPageProps) {
  const { profile } = useAuth();
  const navigate = useNavigate();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const getUserName = () => {
    if (profile?.full_name) {
      return profile.full_name.split(' ')[0];
    }
    return 'there';
  };

  const handleQuickAction = (path: string) => {
    onDismiss();
    navigate(path);
  };

  return (
    <div className="fixed inset-0 z-50 bg-white flex items-center justify-center overflow-hidden">
      {/* Subtle gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50/30" />
      
      {/* Floating decorative elements - hidden on mobile for performance */}
      <div className="hidden md:block absolute top-20 left-20 w-72 h-72 bg-brand-blue/5 rounded-full blur-3xl" />
      <div className="hidden md:block absolute bottom-20 right-20 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl" />

      <div className="relative z-10 w-full max-w-3xl mx-auto px-4 py-6 md:px-6 md:py-12 text-center">
        {/* Sparkle icon */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', duration: 0.6 }}
          className="mb-4 md:mb-8 flex justify-center"
        >
          <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-gradient-to-br from-brand-blue to-blue-600 flex items-center justify-center shadow-lg shadow-brand-blue/25">
            <Sparkles className="w-6 h-6 md:w-8 md:h-8 text-white" />
          </div>
        </motion.div>

        {/* Greeting text */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="mb-6 md:mb-12"
        >
          <p className="text-lg md:text-xl font-medium text-brand-blue mb-1 md:mb-2">
            {getGreeting()}, {getUserName()}
          </p>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
            Where should we start?
          </h1>
        </motion.div>

        {/* Quick action cards */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-6 md:mb-12"
        >
          {quickActions.map((action, index) => (
            <motion.button
              key={action.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + index * 0.1 }}
              onClick={() => handleQuickAction(action.path)}
              className="group relative bg-white border border-slate-200 rounded-xl md:rounded-2xl p-4 md:p-6 text-left transition-all duration-300 hover:border-slate-300 hover:shadow-xl hover:shadow-slate-200/50 hover:-translate-y-1"
            >
              <div className="flex items-start gap-3 md:gap-4">
                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-gradient-to-br ${action.gradient} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                  <action.icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base md:text-lg font-semibold text-slate-900 mb-1 group-hover:text-brand-blue transition-colors">
                    {action.label}
                  </h3>
                  <p className="text-xs md:text-sm text-slate-500">
                    {action.description}
                  </p>
                </div>
                <ArrowRight className="hidden md:block w-5 h-5 text-slate-300 group-hover:text-brand-blue group-hover:translate-x-1 transition-all" />
              </div>
            </motion.button>
          ))}
        </motion.div>

        {/* Skip button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
        >
          <Button
            variant="ghost"
            onClick={onDismiss}
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full px-8"
          >
            Skip for now
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
